import { useState } from "react";
import { useWorkItems, type WorkItemFilters } from "../api/useWorkItems";
import { useQueueStats } from "../api/useQueueStats";
import { QueueFilters } from "./QueueFilters";
import { QueueItem } from "./QueueItem";
import { EmptyState } from "@hub/ui/components/EmptyState";
import { Skeleton } from "@hub/ui/components/ui/skeleton";
import { ScrollArea } from "@hub/ui/components/ui/scroll-area";
import { Inbox } from "lucide-react";

interface QueueListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function QueueList({ selectedId, onSelect }: QueueListProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filters: WorkItemFilters = {
    statuses: statusFilter ? [statusFilter] : [],
  };

  const { items, loading } = useWorkItems(filters);
  const { byStatus } = useQueueStats();

  const filteredItems = search
    ? items.filter((item) => {
        const searchLower = search.toLowerCase();
        return (
          item.type.toLowerCase().includes(searchLower) ||
          item.sourceRef.toLowerCase().includes(searchLower) ||
          Object.values(item.context).some((v) =>
            v.toLowerCase().includes(searchLower)
          )
        );
      })
    : items;

  return (
    <div className="flex flex-col h-full border-r border-gray-200 dark:border-white/10">
      <QueueFilters
        activeStatus={statusFilter}
        onStatusChange={setStatusFilter}
        search={search}
        onSearchChange={setSearch}
        statusCounts={byStatus}
      />

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Your queue is empty"
            description="No work items match the current filters"
          />
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-white/5">
            {filteredItems.map((item) => (
              <QueueItem
                key={item.id}
                item={item}
                isSelected={selectedId === item.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
